import {GetStaticProps, InferGetStaticPropsType} from 'next'
import {useRouter} from 'next/router'
import React from 'react'
import {sessionStorage} from '@mirohq-internal/website-utils'
import getLabels from '../src/api/getLabels'
import {getFirstQuestion} from '../src/api/getQuestions'
import CallToAction from '../src/components/CallToAction'
import {RETURN_URL_STORAGE_KEY} from '../src/components/constants'
import Description from '../src/components/Description'
import Title from '../src/components/Title'

export const getStaticProps: GetStaticProps = async () => {
  const labels = await getLabels('home')
  const firstQuestion = await getFirstQuestion()
  return {
    props: {
      labels,
      firstQuestion,
    },
  }
}

export default function Home({
  labels,
  firstQuestion,
}: InferGetStaticPropsType<typeof getStaticProps>): React.ReactElement {
  const router = useRouter()

  React.useEffect(() => {
    if (router?.query?.returnUrl) {
      sessionStorage.set(RETURN_URL_STORAGE_KEY, router?.query?.returnUrl)
    }
  }, [router])

  const renderGreeting = () => {
    const name = router?.query?.name
    if (name) {
      const content = labels.greeting.replace(/{name}/g, name)

      return <>{content}</>
    }
    return <>{labels.greetingNoName}</>
  }

  return (
    <>
      <HurrayImg></HurrayImg>
      <Title>{renderGreeting()}</Title>
      <Description>{labels.description}</Description>
      <CallToAction href={`/questionnaire/${firstQuestion.code}`}>{labels.cta}</CallToAction>
    </>
  )
}

function HurrayImg(props) {
  return (
    <svg width={211} height={160} fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <g opacity={0.8} filter="url(#prefix__filter0_d)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M29.672 86.632a1.978 1.978 0 012.62.98l2.122 4.663a1.978 1.978 0 01-3.6 1.64l-2.123-4.663a1.978 1.978 0 01.98-2.62z"
          fill="#fff"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M62.387 103.064c.843.522 1.545 1.355 1.941 2.545 1.015 3.05 1.643 6.2 1.643 9.17 0 13.523-10.963 24.486-24.486 24.486-13.523 0-24.485-10.963-24.485-24.486 0-4.282 1.669-7.801 3.169-10.608.345-.646.676-1.249.992-1.823 1.17-2.13 2.123-3.862 2.725-5.88.329-1.101 1.09-2.07 2.083-2.692l-1.239-2.72a6.33 6.33 0 1111.522-5.247l.91 1.998c.157-3.195 3.566-5.654 6.837-4.057 1.849.903 3.585 1.895 5.18 3.018l1.558-2.647a6.33 6.33 0 0111.13 6.016 6.33 6.33 0 012.594 11.577l-2.074 1.35zM33.71 94.742a1.978 1.978 0 00.704-2.467l-2.123-4.662a1.978 1.978 0 00-3.6 1.639l2.123 4.662a1.978 1.978 0 002.896.828z"
          fill="#fff"
        />
        <path
          d="M41.485 134.912c11.12 0 20.134-9.014 20.134-20.133 0-2.385-.51-5.061-1.42-7.795-.1-.297-.496-.365-.722-.148-1.528 1.472-4.098 2.363-4.098 2.363 0-11.376-4.418-17.206-13.289-21.538-.27-.132-.584.077-.582.378.05 7.353-6.405 15.199-6.405 15.199s-3.942-4.272-6.497-5.76c-.215-.124-.479-.002-.55.236-1.857 6.222-6.704 10.776-6.704 17.065 0 11.119 9.014 20.133 20.133 20.133z"
          fill="#FF6575"
        />
        <path
          d="M41.485 134.912c11.12 0 20.134-9.014 20.134-20.133 0-2.385-.51-5.061-1.42-7.795-.1-.297-.496-.365-.722-.148-1.528 1.472-4.098 2.363-4.098 2.363 0-11.376-4.418-17.206-13.289-21.538-.27-.132-.584.077-.582.378.05 7.353-6.405 15.199-6.405 15.199s-3.942-4.272-6.497-5.76c-.215-.124-.479-.002-.55.236-1.857 6.222-6.704 10.776-6.704 17.065 0 11.119 9.014 20.133 20.133 20.133z"
          fill="#FFD02F"
          fillOpacity={0.5}
        />
        <path
          d="M51.255 118.293c.704 5.406-3.108 10.358-8.514 11.062-5.405.703-10.358-3.109-11.061-8.515-.392-3.008 2.057-6.337 2.807-8.777.177-.578.578-.768 1.106-.473l2.227 1.243c.777-.509 2.635-2.344 3.918-5.276.2-.457.647-.521 1.053-.231 3.23 2.307 5.032 6.569 5.252 9.012l1.93-.528c.396-.141.62-.037.764.358.246.674.422 1.384.518 2.125z"
          fill="#FFD952"
        />
        <path
          opacity={0.5}
          d="M32.614 93.094l-2.122-4.662m23.729 2.25l1.972-3.351m2.65 10.487l2.167-1.41"
          stroke="#FF9A52"
          strokeWidth={3.955}
          strokeLinecap="round"
        />
      </g>
      <g filter="url(#prefix__filter1_d)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M94.7 34.774c2.546-.5 5.772-1.06 8.193-1.191 4.047-.22 6.679 3.03 7.717 5.548a11.463 11.463 0 01.501 7.12c1.688 2.517 2.035 5.321 1.691 7.664a11.517 11.517 0 01-.68 2.544c.621 1.33.83 2.787.754 4.075-.161 2.749-1.511 5.53-3.63 7.506-.2.186-.418.354-.65.5-1.311.825-2.463 1.527-4.336 2.14-1.657.541-3.85 1.003-7.29 1.664-3.24.622-6.934.784-10.12.77a4.354 4.354 0 01-3.483 3.617l-11.086 2.13a4.352 4.352 0 01-5.095-3.454l-5.238-27.272A4.352 4.352 0 0165.4 43.04l9.72-1.867c.182-.204.368-.43.556-.682a6.331 6.331 0 01-2.38-11.184 6.331 6.331 0 016.847-10.344l1.982.8c.74.298 1.392.72 1.944 1.233.074-.032.149-.063.226-.093 1.621-.629 3.288-.504 4.75.062 1.128.436 1.939 1.157 2.509 1.849.136-.091.277-.177.423-.259l1.59-.886a6.33 6.33 0 116.167 11.056l-1.591.887c-1.1.613-2.42.915-3.444 1.162-.009-.123.01.125 0 0z"
          fill="#fff"
        />
        <path
          d="M103.129 37.928c-4.385.239-12.575 2.19-12.575 2.19s.022-13.896-3.076-15.094c-2.5-.966-3.526 1.986-3.66 4.14-.58 9.357-5.28 16.737-9.471 17.542l4.155 21.632s10.458 1.113 17.647-.268c6.99-1.343 7.655-1.658 10.13-3.215 2.702-2.518 3.239-7.013.199-8.02 1.432-.275 4.073-5.976-.375-9.685 2.555-4.518-.275-9.368-2.974-9.221z"
          fill="#4262FF"
        />
        <path
          d="M90.595 40.108c.01.06.023.12.04.18v.004-.003.004a7.164 7.164 0 01.063.28c.044.212.102.53.15.924.099.796.156 1.867.012 3.002-.284 2.23-1.297 4.565-4.29 5.826a1.978 1.978 0 101.536 3.645c4.687-1.975 6.278-5.825 6.677-8.972a16.236 16.236 0 00-.009-3.986 15.774 15.774 0 00-.28-1.593l-.026-.106-.01-.034-.002-.011-.002-.005-.028.005-1.88.508 1.862-.504c-2.008.412-3.549.773-3.813.836zM66.222 47.314l11.086-2.13 5.238 27.273-11.086 2.129-5.238-27.272z"
          fill="#A2B2FF"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M75.942 24.093a1.978 1.978 0 001.094 2.574l1.982.8a1.978 1.978 0 001.48-3.669l-1.982-.8a1.978 1.978 0 00-2.574 1.095zm22.437 2.14a1.978 1.978 0 00-2.69-.764l-1.591.887a1.978 1.978 0 101.926 3.455l1.591-.887a1.978 1.978 0 00.764-2.69zm-18.57 7.588a1.978 1.978 0 00-2.315-1.569l-.72.138a1.978 1.978 0 00.747 3.885l.72-.139a1.978 1.978 0 001.569-2.315z"
          fill="#A2B2FF"
        />
      </g>
      <g opacity={0.4} filter="url(#prefix__filter2_d)">
        <path
          d="M142.147 80.813c.025 10.226 12.818 21.707 20.203 26.582.433.286.98.334 1.456.127 8.12-3.518 22.713-12.603 24.513-22.67 2.181-12.197-7.563-14.972-10.14-15.197-2.061-.18-7.74-.57-12.241 5.123-3.445-6.389-9.105-6.991-11.167-7.171-2.577-.226-12.653.815-12.624 13.206z"
          fill="#FFBEC5"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M146.2 98.961c-4.285-4.942-8.389-11.392-8.405-18.138-.018-7.606 3.189-12.405 7.374-15.03 3.838-2.41 8.011-2.694 9.982-2.522 1.895.166 7.037.702 11.355 5.012 5.001-3.495 10.158-3.13 12.053-2.964 1.971.173 6.031 1.178 9.393 4.216 3.665 3.313 5.99 8.595 4.651 16.083-1.187 6.641-6.349 12.28-11.427 16.403-5.242 4.258-11.274 7.604-15.639 9.495a5.884 5.884 0 01-5.584-.489c-3.97-2.62-9.329-6.963-13.753-12.066zm16.151 8.434c.433.286.98.334 1.456.128 8.119-3.519 22.712-12.604 24.512-22.67 2.181-12.198-7.562-14.973-10.139-15.198-1.584-.139-5.302-.401-8.981 2.066-1.111.744-2.218 1.737-3.261 3.057-.799-1.481-1.716-2.651-2.681-3.577-3.195-3.068-6.902-3.456-8.485-3.594-2.577-.226-12.654.815-12.625 13.206.025 10.226 12.818 21.707 20.204 26.582z"
          fill="#fff"
        />
        <path
          d="M142.147 80.813c.025 10.226 12.818 21.707 20.203 26.582.433.286.98.334 1.456.127 8.12-3.518 22.713-12.603 24.513-22.67 2.181-12.197-7.563-14.972-10.14-15.197-2.061-.18-7.74-.57-12.241 5.123-3.445-6.389-9.105-6.991-11.167-7.171-2.577-.226-12.653.815-12.624 13.206z"
          fill="#FF6575"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M178.715 78.982c-1.099-.927-2.822-1.324-4.998-.156a1.978 1.978 0 01-1.871-3.486c3.473-1.863 6.968-1.447 9.418.617 2.376 2.002 3.499 5.326 2.878 8.78a1.978 1.978 0 01-3.893-.7c.4-2.224-.36-4.067-1.534-5.055z"
          fill="#FFBEC5"
        />
      </g>
      <defs>
        <filter
          id="prefix__filter0_d"
          x={0.784}
          y={68.838}
          width={82.773}
          height={90.698}
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity={0} result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy={4.054} />
          <feGaussianBlur stdDeviation={8.108} />
          <feColorMatrix values="0 0 0 0 0.0196078 0 0 0 0 0 0 0 0 0 0.219608 0 0 0 0.08 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
        </filter>
        <filter
          id="prefix__filter1_d"
          x={40.783}
          y={0.838}
          width={92.258}
          height={99.114}
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity={0} result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy={4.054} />
          <feGaussianBlur stdDeviation={8.108} />
          <feColorMatrix values="0 0 0 0 0.0196078 0 0 0 0 0 0 0 0 0 0.219608 0 0 0 0.08 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
        </filter>
        <filter
          id="prefix__filter2_d"
          x={118.96}
          y={49.726}
          width={91.618}
          height={84.93}
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity={0} result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy={4.054} />
          <feGaussianBlur stdDeviation={8.108} />
          <feColorMatrix values="0 0 0 0 0.0196078 0 0 0 0 0 0 0 0 0 0.219608 0 0 0 0.08 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
        </filter>
      </defs>
    </svg>
  )
}
